import { type ClassValue, clsx } from 'clsx'
import type { CtxAsync } from '@/lib/db-context'
import { twMerge } from 'tailwind-merge'
import { decodeSearch } from '@swan-io/chicane'

import { EQUIPMENT } from './constants'
import { FormEvent } from 'react'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function mapToList<T, I>(m: Map<I, T>): Array<T & { id: I }> {
  return Array.from(m, ([id, entity]) => ({ ...entity, id }))
}

type ProgressionCell = {
  col: number
  row: number
}
type CompletedSet = any

type ProgressionCellWithSets = ProgressionCell & {
  completedSets: CompletedSet[]
}

export const displayWorkoutExercise = (
  cells: ProgressionCellWithSets[] | undefined,
  currentWeek: number
): [row: ProgressionCellWithSets[], isFinished: boolean] => {
  let isFinished = false
  let row: ProgressionCellWithSets[] = []
  if (!cells) {
    return [row, isFinished]
  }

  // the currentWeek should
  const columns = cells.reduce((acc, curr) => {
    if (curr.col > acc) {
      return curr.col
    }
    return acc
  }, 0)

  let targetColumn = currentWeek

  if (targetColumn > columns) {
    if (columns === 0) {
      targetColumn = 0
    } else {
      targetColumn = 0
    }
    // need to find that actual target column still
  }

  row = cells
    .filter((cell) => cell.col === targetColumn)
    .sort((a, z) => (a.row >= z.row ? 1 : -1))
  isFinished =
    row.length > 0 && row.every((cell) => cell.completedSets.length > 0)

  return [row, isFinished]
}

export function roundDownTo(value: number, multiple = 5): number {
  return Math.floor(value / multiple) * multiple
}

export function estimateOneRepMaximum(weight: number, reps: number): number {
  return Number(weight) * (1 + Number(reps) / 30)
}

export function getIncrementForEquipment(
  equipment: string | null,
  defaultInc = 2.5
): number {
  if (!equipment) return defaultInc
  const found = EQUIPMENT.get(equipment)
  if (found) {
    return found.inc
  } else {
    return defaultInc
  }
}

/**
 * @description Takes two objects of the same schema and returns values that are different from the first object.
 */
export function diffObjects<T extends object>(a: T, b: T): T {
  return Object.keys(a).reduce((acc, key) => {
    const k = key as keyof T
    if (a[k] !== b[k] && b[k] !== undefined) {
      acc[k] = b[k]
    }
    return acc
  }, {} as T)
}

export function getFormData<T extends { [key: string]: unknown }>(
  e: FormEvent<HTMLFormElement>
) {
  const data = Object.fromEntries(
    new FormData(e.currentTarget, (e.nativeEvent as any).submitter)
  )

  return data as T
}

export function getSearchDate(search: string) {
  const decoded = decodeSearch(search)

  if (decoded.date && typeof decoded.date === 'string') {
    return new Date(decoded.date)
  } else {
    return new Date()
  }
}

export function insertCSV(ctx: CtxAsync, raw: string, tableName: string) {
  const rows = raw.split('\n')
  const headers = rows[0]
  const columns = headers
    .replaceAll('"', '')
    .split(',')
    .map((c) => c.trim())
  const data = rows.slice(1)
  return ctx.db.execMany(
    data.map((row) => {
      const data = row
        .replaceAll('"', '')
        .split(',')
        .map((c) => c.trim())
      const rowColumns = columns
      const dataRow = columns.reduce((acc, col, idx) => {
        const value = data[idx]
        if (col === 'id' && (value === '' || value === undefined)) {
          rowColumns[idx] = null as any
          return acc
        }
        if (/\d{4}-\d{2}-\d{2}/g.test(value)) {
          acc += `'${value}'`
        } else if (!Number.isNaN(parseInt(value))) {
          acc += `${value}`
        } else if (/undefined/g.test(value)) {
          acc += `null`
        } else {
          acc += `'${value}'`
        }
        if (idx < columns.length - 1) {
          acc += ','
        }
        return acc
      }, '')

      const q = `
    INSERT INTO "${tableName}" 
    (${rowColumns
      .filter((v) => v !== null)
      .map((v) => `"${v}"`)
      .join(',')})
    VALUES 
    (${dataRow});
    `
      return q
    })
  )
}
